/**
 * Brand Reveal Slide
 *
 * Slide 1: "We found your brand!" - Shows brand discovery results.
 */

import { motion } from 'framer-motion'
import { Building2, Tag, Globe } from 'lucide-react'
import type { OnboardingArtifacts } from '../../../contexts/OnboardingContext'

interface BrandRevealSlideProps {
  artifacts: OnboardingArtifacts
}

export default function BrandRevealSlide({ artifacts }: BrandRevealSlideProps) {
  // Handle multiple possible artifact structures
  const brand = artifacts.brand as {
    brand_name?: string
    brand_logo_url?: string
    brand_description?: string
    brand_category?: string
    brand_url?: string
    brand_profile?: {
      brand_name?: string
      logo_url?: string
      product_focus?: string
      value_proposition?: string
      domain?: string
    }
  } | null

  // Try both direct fields and nested brand_profile
  const brandName = brand?.brand_name || brand?.brand_profile?.brand_name || 'Your Brand'
  const logoUrl = brand?.brand_logo_url || brand?.brand_profile?.logo_url
  const description = brand?.brand_description || brand?.brand_profile?.value_proposition
  const category = brand?.brand_category || brand?.brand_profile?.product_focus
  const url = brand?.brand_url || brand?.brand_profile?.domain

  return (
    <div className="flex flex-col items-center text-center">
      {/* Logo reveal */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5, type: 'spring' }}
        className="mb-8"
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={brandName}
            className="w-28 h-28 rounded-2xl object-cover shadow-xl ring-4 ring-white/10"
          />
        ) : (
          <div className="w-28 h-28 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-xl">
            <Building2 className="w-14 h-14 text-white" />
          </div>
        )}
      </motion.div>

      {/* Title */}
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-3xl font-bold text-tf-text mb-2"
      >
        We Found Your Brand!
      </motion.h2>

      {/* Brand name */}
      <motion.p
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-xl text-primary font-semibold mb-4"
      >
        {brandName}
      </motion.p>

      {/* Category badge */}
      {category && (
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 text-purple-400 mb-6"
        >
          <Tag className="w-4 h-4" />
          <span className="text-sm font-medium">{category}</span>
        </motion.div>
      )}

      {/* Description */}
      {description && (
        <motion.p
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-tf-text-muted max-w-md leading-relaxed"
        >
          {description}
        </motion.p>
      )}

      {/* URL */}
      {url && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-6 inline-flex items-center gap-2 text-sm text-tf-text-muted"
        >
          <Globe className="w-4 h-4" />
          <span>{url}</span>
        </motion.div>
      )}
    </div>
  )
}
